import { defineStore } from 'pinia';
import { abxClient } from './abx.js';
import { useSiteStore } from './site.js';
import { useSupportStore } from './support.js';
import { useUserStore } from './user.js';

// Company-scoped ABX data for the All my sites page and the sidebar.
// The server derives the company and the authorized sites from the session, so no company ID is sent.
const BASE_PATH = '/programs/abx-stewardship';
// Logout keeps these keys, like the other saved filters. Each key holds the user and company IDs.
export const ABX_COMPARE_SELECTION_KEY_PREFIX = 'abxCompareSelection';
let nextRequestToken = 0;

function emptyState() {
    return {
        contextKey: null,
        sites: [],
        canCompare: false,
        sitesLoaded: false,
        sitesLoading: false,
        sitesError: null,
        sitesPromise: null,
        overview: null,
        overviewLoading: false,
        overviewError: null,
        overviewToken: null,
        comparison: null,
        comparisonLoading: false,
        comparisonError: null,
        comparisonToken: null,
        openingSiteId: null,
    };
}

function selectionKey(contextKey) {
    return contextKey
        ? `${ABX_COMPARE_SELECTION_KEY_PREFIX}:${contextKey}`
        : null;
}

export const useAbxCompanyStore = defineStore('abxCompany', {
    state: emptyState,
    getters: {
        enabledSites: (state) =>
            state.sites.filter((site) => site.abxState === 'ENABLED'),
        hasAbxAccess() {
            return this.enabledSites.length > 0;
        },
        isMultiSite: (state) => state.sites.length > 1,
    },
    actions: {
        // The context is the user and the company (or the support company). A change clears all data.
        setContext(userId, companyId) {
            const key = userId && companyId ? `${userId}:${companyId}` : null;
            if (key === this.contextKey) return;
            this.$patch(emptyState());
            this.contextKey = key;
        },
        // Sets the context from the signed-in user and the support company of this tab.
        syncSession() {
            const userStore = useUserStore();
            this.setContext(
                userStore.user?.id,
                useSupportStore().companyId ||
                    userStore.company?.id ||
                    userStore.user?.company_id
            );
        },
        clear() {
            this.$patch(emptyState());
        },
        // Opens one site in the workspace. The All my sites page reacts to other site changes only.
        openSite(router, siteId, location = { name: 'ABX Overview' }) {
            this.openingSiteId = String(siteId);
            useSiteStore().setGlobalSiteId(String(siteId));
            return router.push(location);
        },
        loadSites({ force = false } = {}) {
            if (!this.contextKey) return Promise.resolve([]);
            if (this.sitesPromise) return this.sitesPromise;
            if (this.sitesLoaded && !force) return Promise.resolve(this.sites);
            const contextKey = this.contextKey;
            this.sitesLoading = true;
            this.sitesError = null;
            this.sitesPromise = abxClient()
                .request(`${BASE_PATH}/sites`)
                .then((response) => {
                    if (this.contextKey !== contextKey) return [];
                    this.sites = response?.items || [];
                    this.canCompare = Boolean(response?.canCompare);
                    this.sitesLoaded = true;
                    return this.sites;
                })
                .catch((error) => {
                    if (this.contextKey === contextKey) this.sitesError = error;
                    throw error;
                })
                .finally(() => {
                    if (this.contextKey === contextKey) {
                        this.sitesLoading = false;
                        this.sitesPromise = null;
                    }
                });
            return this.sitesPromise;
        },
        async loadOverview() {
            const contextKey = this.contextKey;
            const token = ++nextRequestToken;
            this.overviewToken = token;
            this.overviewLoading = true;
            this.overviewError = null;
            const current = () =>
                this.contextKey === contextKey && this.overviewToken === token;
            try {
                const response = await abxClient().request(
                    `${BASE_PATH}/sites-overview`
                );
                if (current()) this.overview = response;
                return response;
            } catch (error) {
                if (current()) this.overviewError = error;
                throw error;
            } finally {
                if (current()) this.overviewLoading = false;
            }
        },
        async loadComparison({ siteIds, period, rateBasis = 1000 }) {
            const contextKey = this.contextKey;
            const token = ++nextRequestToken;
            this.comparisonToken = token;
            this.comparisonLoading = true;
            this.comparisonError = null;
            const current = () =>
                this.contextKey === contextKey &&
                this.comparisonToken === token;
            try {
                const response = await abxClient().request(
                    `${BASE_PATH}/site-comparison`,
                    {
                        query: {
                            siteIds: siteIds.join(','),
                            period: JSON.stringify(period),
                            rateBasis,
                        },
                    }
                );
                if (current()) this.comparison = response;
                return response;
            } catch (error) {
                if (current()) this.comparisonError = error;
                throw error;
            } finally {
                if (current()) this.comparisonLoading = false;
            }
        },
        // Cases behind one comparison value. The view keeps the result, so no store state.
        loadDrillThrough({
            siteId,
            period,
            metric,
            infection,
            page = 1,
            pageSize = 25,
        }) {
            return abxClient().request(
                `/sites/${encodeURIComponent(siteId)}${BASE_PATH}/abx-cases`,
                {
                    query: {
                        period: JSON.stringify(period),
                        metric,
                        infection,
                        page,
                        pageSize,
                    },
                }
            );
        },
        // The compare choices (sites, metrics, period) are kept per user and company on this device.
        readSelection() {
            const key = selectionKey(this.contextKey);
            if (!key || typeof localStorage === 'undefined') return null;
            try {
                const value = JSON.parse(localStorage.getItem(key) || 'null');
                return value && typeof value === 'object' ? value : null;
            } catch {
                return null;
            }
        },
        writeSelection(selection) {
            const key = selectionKey(this.contextKey);
            if (!key || typeof localStorage === 'undefined') return;
            try {
                localStorage.setItem(key, JSON.stringify(selection));
            } catch {
                // Storage can be full or blocked; the page still works without it.
            }
        },
    },
});
